import { DataLayer, calcPrice } from "@grandvisionhq/analytics";
const getPurchaseType = (lineItem) => {
  if (lineItem.type === "contact-lenses" || lineItem.type === "solutions") {
    return lineItem.isSubscription ? DataLayer.DataLayerTypes.PurchaseType.Subscription : DataLayer.DataLayerTypes.PurchaseType.OneTime;
  }
  return DataLayer.DataLayerTypes.PurchaseType.OneTime;
};
const getLineItemType = (lineItem) => {
  if (lineItem.type === "contact-lenses" || lineItem.type === "solutions") {
    return lineItem.isSubscription ? DataLayer.DataLayerTypes.LineItemType.Subscription : DataLayer.DataLayerTypes.LineItemType.Single;
  }
  return DataLayer.DataLayerTypes.LineItemType.Single;
};
const getSharedAttributes = (lineItem, quantity = 1) => {
  const { sku, price } = lineItem;
  const isOnStock = lineItem.availability && Object.values(lineItem.availability.channels).some(
    ({ isOnStock: isOnStock2, availableQuantity }) => isOnStock2 && availableQuantity > 0
  );
  const exclusiveBrand = Boolean(lineItem.brand.exclusive);
  return {
    name: lineItem.name,
    id: sku,
    price: calcPrice(price.unit.gross),
    dimension143: getPurchaseType(lineItem),
    dimension144: getLineItemType(lineItem),
    dimension145: "true",
    dimension190: lineItem.erpId || "",
    dimension193: exclusiveBrand ? DataLayer.DataLayerTypes.BrandType.OwnBrand : DataLayer.DataLayerTypes.BrandType.ExternalBrand,
    dimension194: isOnStock ? DataLayer.DataLayerTypes.Stock.InStock : DataLayer.DataLayerTypes.Stock.OutOfStock,
    metric140: calcPrice(price.unit.original),
    metric141: calcPrice(price.unit.discount),
    metric142: calcPrice(price.unit.taxAmount),
    quantity
  };
};
const getSunglassesAttributes = (lineItem, quantity) => {
  const { brand, frame, gender, sku } = lineItem;
  const genderValue = gender && gender.map((value) => value.toLowerCase()).join();
  return {
    ...getSharedAttributes(lineItem, quantity),
    category: DataLayer.DataLayerTypes.Category.Sunglasses,
    brand: brand.name || "",
    variant: frame?.frameShape?.toLowerCase() || "",
    dimension16: genderValue || "",
    dimension140: sku,
    dimension146: frame?.frameMaterial?.join().toLowerCase() || "",
    dimension147: frame?.frameColor?.join().toLowerCase() || "",
    dimension191: frame?.frameType?.toLocaleLowerCase() || "",
    dimension192: frame?.faceShape?.toLowerCase() || ""
  };
};
const getReadyReadersAttributes = (lineItem, quantity = 1) => {
  const { brand, frame, gender, sku } = lineItem;
  const genderValue = gender && gender.map((value) => value.toLowerCase()).join();
  return {
    ...getSharedAttributes(lineItem, quantity),
    category: DataLayer.DataLayerTypes.Category.ReadyReaders,
    brand: brand.name || "",
    variant: frame?.frameShape?.toLowerCase() || "",
    dimension16: genderValue || "",
    dimension140: sku,
    dimension146: frame?.frameMaterial?.join().toLowerCase() || "",
    dimension147: frame?.frameColor?.join().toLowerCase() || "",
    dimension191: frame?.frameType?.toLocaleLowerCase() || "",
    dimension192: frame?.faceShape?.toLowerCase() || ""
  };
};
const getAccessoriesLineItem = (lineItem, quantity = 1) => {
  const { brand, accessoryType, accessoryColor, material, gender, sku } = lineItem;
  const genderValue = gender && gender.map((value) => value.toLowerCase()).join();
  return {
    ...getSharedAttributes(lineItem, quantity),
    category: DataLayer.DataLayerTypes.Category.Accessories,
    brand: brand.name || "",
    variant: accessoryType || "",
    dimension16: genderValue || "",
    dimension140: sku,
    dimension146: material || "",
    dimension147: accessoryColor?.join().toLowerCase() || ""
  };
};
const getContactLensesAttributes = (lineItem, quantity) => ({
  ...getSharedAttributes(lineItem, quantity),
  name: lineItem.name,
  category: DataLayer.DataLayerTypes.Category.ContactLenses,
  brand: lineItem.brand.name || "",
  variant: lineItem.lensType || "",
  dimension140: lineItem.sku,
  dimension146: lineItem.material || "",
  dimension148: lineItem.replacementFrequency?.toLowerCase() || "",
  deliveryInterval: lineItem.subscriptionDetails?.deliveryInterval
});
const getSolutionsAttributes = (lineItem, quantity) => ({
  ...getSharedAttributes(lineItem, quantity),
  name: lineItem.name,
  category: DataLayer.DataLayerTypes.Category.Solutions,
  brand: lineItem.brand.name || "",
  variant: lineItem.solutionVolume + "ml",
  dimension140: lineItem.sku,
  dimension146: lineItem.solutionType,
  deliveryInterval: lineItem.subscriptionDetails?.deliveryInterval
});
const getPrescriptionGlassesAttributes = (lineItem, quantity = 1) => {
  const { frame, price, productType } = lineItem;
  const { brand } = frame;
  const category = productType === "sunglasses" ? DataLayer.DataLayerTypes.Category.Sunglasses : DataLayer.DataLayerTypes.Category.Optical;
  const name = lineItem.frame.name || "";
  const isOnStock = lineItem.availability && Object.values(lineItem.availability.channels).some(
    ({ isOnStock: isOnStock2, availableQuantity }) => isOnStock2 && availableQuantity > 0
  );
  const exclusiveBrand = Boolean(brand.exclusive);
  return {
    category,
    name,
    brand: frame.brand.name || "",
    id: frame.sku,
    price: calcPrice(price.unit.gross),
    variant: frame.frameShape?.toLowerCase() || "",
    dimension16: frame.gender?.toLowerCase() || "",
    dimension140: frame.sku,
    dimension143: getPurchaseType(lineItem),
    dimension144: DataLayer.DataLayerTypes.LineItemType.Bundle,
    dimension145: "true",
    dimension146: frame.frameMaterial || "",
    dimension147: frame.frameColor || "",
    dimension190: lineItem.erpId || "",
    dimension191: frame.frameType?.toLowerCase() || "",
    dimension192: frame.faceShape?.toLowerCase() || "",
    dimension193: exclusiveBrand ? DataLayer.DataLayerTypes.BrandType.OwnBrand : DataLayer.DataLayerTypes.BrandType.ExternalBrand,
    dimension194: isOnStock ? DataLayer.DataLayerTypes.Stock.InStock : DataLayer.DataLayerTypes.Stock.OutOfStock,
    metric140: calcPrice(price.unit.original),
    metric141: calcPrice(price.unit.discount),
    metric142: calcPrice(price.unit.taxAmount),
    quantity
  };
};
const mapClientModelToProductObject = (lineItem, quantity) => {
  switch (lineItem.type) {
    case "sunglasses":
      return getSunglassesAttributes(lineItem, quantity);
    case "contact-lenses":
      return getContactLensesAttributes(lineItem, quantity);
    case "solutions":
      return getSolutionsAttributes(lineItem, quantity);
    case "prescription-glasses":
      return getPrescriptionGlassesAttributes(lineItem, quantity);
    case "ready-readers":
      return getReadyReadersAttributes(lineItem, quantity);
    case "accessories":
      return getAccessoriesLineItem(lineItem, quantity);
    default:
      return void 0;
  }
};
export {
  mapClientModelToProductObject
};
//# sourceMappingURL=client-model-to-product-object.js.map
